import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  BulkVerificationRunView,
  getActiveBulkVerificationRun,
  getBulkVerificationRun,
} from 'api/bitedb/entities/bulkVerificationRun.service';
import { entityKeys } from 'api/bitedb/entities/entities.keys';
import { STATS_QUERY_KEYS } from 'api/bitedb/stats/stats.hooks';
import {
  forgetBulkVerificationRun,
  rememberBulkVerificationRun,
  storedBulkVerificationRunId,
} from './bulkVerificationRunStorage';

export const BULK_VERIFICATION_POLL_INTERVAL_MS = 5000;

/** The database page's view of its Bulk Verification Run, shared by the strip, the badge and the tile. */
export interface BulkVerificationRunTracker {
  /** The environment kill switch answered VERIFICATION_DISABLED. */
  verificationDisabled: boolean;
  /** The run to show: in progress, or finished and not yet dismissed. */
  run: BulkVerificationRunView | null;
  isRunning: boolean;
  /** The last poll failed; `run` keeps the last numbers. */
  isPollFailing: boolean;
  /** Follow a run this page just started. */
  follow: (runId: string) => void;
  /** Look the database's active run up again, e.g. after a 409 at start. */
  followActive: () => void;
  /** Hide a finished run's summary, here and after a reload. */
  dismiss: () => void;
  /** Failed Database Entities the summary asked to run again, until the tile takes them up. */
  retryEntityIds: string[] | null;
  retryFailed: (databaseEntityIds: string[]) => void;
  clearRetry: () => void;
}

interface Following {
  runId: string;
  /** The view the lookup already returned, so the first poll waits a full interval. */
  initial?: BulkVerificationRunView;
}

/**
 * Reads the database's active Bulk Verification Run on mount, falls back to the
 * last run stored for the database, and polls a running one every 5 seconds
 * until it completes. The entity list refetches when a run it watched finishes.
 */
export const useBulkVerificationRun = (
  databaseId: string | undefined,
  enabled = true,
): BulkVerificationRunTracker => {
  const queryClient = useQueryClient();
  const [verificationDisabled, setVerificationDisabled] = useState(false);
  const [run, setRun] = useState<BulkVerificationRunView | null>(null);
  const [isPollFailing, setIsPollFailing] = useState(false);
  const [following, setFollowing] = useState<Following | null>(null);
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const [retryEntityIds, setRetryEntityIds] = useState<string[] | null>(null);
  const sawRunning = useRef(false);
  const [trackedDatabaseId, setTrackedDatabaseId] = useState(databaseId);

  // Reset during render, not in an effect, so the polling effect never runs
  // once with the previous database's run and the new databaseId.
  if (trackedDatabaseId !== databaseId) {
    setTrackedDatabaseId(databaseId);
    setVerificationDisabled(false);
    setRun(null);
    setIsPollFailing(false);
    setFollowing(null);
    setRetryEntityIds(null);
    sawRunning.current = false;
  }

  useEffect(() => {
    if (!databaseId || !enabled) return undefined;
    let cancelled = false;
    getActiveBulkVerificationRun(databaseId).then((outcome) => {
      if (cancelled) return;
      if (!outcome.ok && outcome.error.refusal === 'verification-disabled') {
        setVerificationDisabled(true);
        return;
      }
      setVerificationDisabled(false);
      if (outcome.ok && outcome.value) {
        rememberBulkVerificationRun(databaseId, outcome.value.runId);
        setFollowing({ runId: outcome.value.runId, initial: outcome.value });
        return;
      }
      const storedRunId = storedBulkVerificationRunId(databaseId);
      if (storedRunId) setFollowing({ runId: storedRunId });
    });
    return () => {
      cancelled = true;
    };
  }, [databaseId, enabled, lookupAttempt]);

  useEffect(() => {
    if (!following || !databaseId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;

    const show = (view: BulkVerificationRunView): boolean => {
      setRun(view);
      setIsPollFailing(false);
      if (view.status === 'running') {
        sawRunning.current = true;
        return true;
      }
      if (sawRunning.current) {
        sawRunning.current = false;
        queryClient.invalidateQueries({
          queryKey: entityKeys.entitiesByDatabasePrefix(databaseId),
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      }
      return false;
    };

    const poll = async () => {
      const outcome = await getBulkVerificationRun(following.runId);
      if (cancelled) return;
      if (outcome.ok) {
        if (show(outcome.value)) {
          timer = setTimeout(poll, BULK_VERIFICATION_POLL_INTERVAL_MS);
        }
        return;
      }
      if (outcome.error.refusal === 'not-found') {
        forgetBulkVerificationRun(databaseId);
        setRun(null);
        setIsPollFailing(false);
        return;
      }
      setIsPollFailing(true);
      timer = setTimeout(poll, BULK_VERIFICATION_POLL_INTERVAL_MS);
    };

    if (following.initial) {
      if (show(following.initial)) {
        timer = setTimeout(poll, BULK_VERIFICATION_POLL_INTERVAL_MS);
      }
    } else {
      poll();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [following, databaseId, queryClient]);

  const follow = useCallback(
    (runId: string) => {
      if (databaseId) rememberBulkVerificationRun(databaseId, runId);
      sawRunning.current = true;
      setFollowing({ runId: runId });
    },
    [databaseId],
  );

  const followActive = useCallback(() => {
    setLookupAttempt((attempt) => attempt + 1);
  }, []);

  const dismiss = useCallback(() => {
    if (databaseId) forgetBulkVerificationRun(databaseId);
    setFollowing(null);
    setRun(null);
    setIsPollFailing(false);
  }, [databaseId]);

  const retryFailed = useCallback((databaseEntityIds: string[]) => {
    setRetryEntityIds(databaseEntityIds.length ? databaseEntityIds : null);
  }, []);

  const clearRetry = useCallback(() => setRetryEntityIds(null), []);

  return {
    verificationDisabled: verificationDisabled,
    run: run,
    isRunning: run?.status === 'running',
    isPollFailing: isPollFailing,
    follow: follow,
    followActive: followActive,
    dismiss: dismiss,
    retryEntityIds: retryEntityIds,
    retryFailed: retryFailed,
    clearRetry: clearRetry,
  };
};
